import API_ENDPOINTS from "../components/config/apiConfig";
import { getFirebaseToken } from "../utils/auth";

// Helper to get Firebase auth token
async function getAuthToken() {
  return await getFirebaseToken();
}

function normalizeEventServiceItem(item) {
  const service = item?.listingService && typeof item.listingService === "object"
    ? item.listingService
    : item;

  if (!service || typeof service !== "object") {
    return null;
  }

  const ratings = Array.isArray(item?.ratings)
    ? item.ratings
    : Array.isArray(service?.ratings)
      ? service.ratings
      : [];

  const ratingValues = ratings
    .map((rating) => Number(rating?.ratingValue))
    .filter((value) => Number.isFinite(value) && value > 0);

  const ratingCount = ratingValues.length;
  const averageRating = ratingCount > 0
    ? ratingValues.reduce((sum, value) => sum + value, 0) / ratingCount
    : 0;

  return {
    ...service,
    ratings,
    ratingCount,
    averageRating,
  };
}

function normalizeEventServicesResponse(data) {
  const source = Array.isArray(data)
    ? data.flat(Infinity)
    : data
      ? [data]
      : [];

  return source
    .map(normalizeEventServiceItem)
    .filter((item) => item && item.esId);
}

function resolveResponseCode(data, fallbackStatus) {
  const code = Number(data?.responseCode ?? data?.responsecode);
  if (Number.isFinite(code) && code > 0) {
    return code;
  }
  return fallbackStatus || 500;
}

// Add Event Service API
export async function addEventServiceApi(payload) {
  try {
    const token = await getAuthToken();
    const response = await fetch(API_ENDPOINTS.ADD_EVENT_SERVICE, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    return {
      success: response.ok && data.responseCode === 200,
      data: data.responseData || data,
      responseMessage: data.responseMessage || data.message,
      responsecode: data.responsecode
    };
  } catch (error) {
    console.error("Error adding event service:", error);
    return {
      success: false,
      error: error.message,
      responseMessage: "Failed to add event service",
      responsecode: -1
    };
  }
}

// Update Event Service API
export async function updateEventServiceApi(payload) {
  try {
    const token = await getAuthToken();
    const esId = payload.esId;
    const response = await fetch(API_ENDPOINTS.UPDATE_EVENT_SERVICE_BY_ID(esId), {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    return {
      success: response.ok && data.responseCode === 200,
      data: data.responseData || data,
      responseMessage: data.responseMessage || data.message,
      responsecode: data.responsecode
    };
  } catch (error) {
    console.error("Error updating event service:", error);
    return {
      success: false,
      error: error.message,
      responseMessage: "Failed to update event service",
      responsecode: -1
    };
  }
}

// Get Event Services by Service Provider ID
export async function getEventServicesBySpIdApi(spId) {
  try {
    const token = await getAuthToken();
    const response = await fetch(API_ENDPOINTS.GET_EVENT_SERVICES_BY_SP_ID(spId), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();
    const normalizedData = normalizeEventServicesResponse(data?.responseData || []);
    const responseCode = resolveResponseCode(data, response.status);
    return {
      success: response.ok && responseCode === 200,
      data: normalizedData,
      responseData: normalizedData,
      responseCode,
      responseMessage: data?.responseMessage || data?.message,
      responsecode: responseCode
    };
  } catch (error) {
    console.error("Error fetching event services:", error);
    return {
      success: false,
      data: [],
      responseData: [],
      responseCode: -1,
      error: error.message,
      responseMessage: "Failed to fetch event services",
      responsecode: -1
    };
  }
}

// Get Event Service by ID
export async function getEventServiceByIdApi(esId) {
  try {
    const token = await getAuthToken();
    const response = await fetch(API_ENDPOINTS.GET_EVENT_SERVICE_BY_ID(esId), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();
    const normalizedItem = normalizeEventServicesResponse(data.responseData || null)[0] || null;
    const responseCode = resolveResponseCode(data, response.status);
    return {
      success: response.ok && responseCode === 200,
      data: normalizedItem,
      responseData: normalizedItem,
      responseCode,
      responseMessage: data.responseMessage || data.message,
      responsecode: responseCode
    };
  } catch (error) {
    console.error("Error fetching event service:", error);
    return {
      success: false,
      data: null,
      responseData: null,
      responseCode: -1,
      error: error.message,
      responseMessage: "Failed to fetch event service",
      responsecode: -1
    };
  }
}

// Delete Event Service by ID
export async function deleteEventServiceApi(esId) {
  try {
    const token = await getAuthToken();
    const response = await fetch(API_ENDPOINTS.DELETE_EVENT_SERVICE_BY_ID(esId), {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();
    return {
      success: response.ok && data.responseCode === 200,
      data: data.responseData || data,
      responseMessage: data.responseMessage || data.message,
      responsecode: data.responsecode
    };
  } catch (error) {
    console.error("Error deleting event service:", error);
    return {
      success: false,
      error: error.message,
      responseMessage: "Failed to delete event service",
      responsecode: -1
    };
  }
}
